/**
 * Safe DOM measurement utilities to prevent "Cannot read properties of null" errors
 */

export interface SafeRect {
  top: number;
  left: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
}

const DEFAULT_RECT: SafeRect = {
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  width: 0,
  height: 0
};

/**
 * Safely get bounding client rect of an element
 */
export function safeBoundingClientRect(element: HTMLElement | SVGElement | null): SafeRect {
  try {
    if (!element || !element.isConnected || !document.contains(element)) {
      return DEFAULT_RECT;
    }
    
    return element.getBoundingClientRect();
  } catch (error) {
    console.warn('Failed to get bounding client rect:', error);
    return DEFAULT_RECT;
  }
}

/**
 * Safely get element dimensions
 */
export function safeDimensions(element: HTMLElement | null): { width: number; height: number; offsetWidth: number; offsetHeight: number } {
  try {
    if (!element || !element.isConnected || !document.contains(element)) {
      return { width: 0, height: 0, offsetWidth: 0, offsetHeight: 0 };
    }
    
    return {
      width: element.clientWidth || 0,
      height: element.clientHeight || 0,
      offsetWidth: element.offsetWidth || 0,
      offsetHeight: element.offsetHeight || 0
    };
  } catch (error) {
    console.warn('Failed to get element dimensions:', error);
    return { width: 0, height: 0, offsetWidth: 0, offsetHeight: 0 };
  }
}

/**
 * Safely get scroll dimensions
 */
export function safeScrollDimensions(element: HTMLElement | null): { scrollWidth: number; scrollHeight: number; scrollTop: number; scrollLeft: number } {
  try {
    if (!element || !element.isConnected || !document.contains(element)) {
      return { scrollWidth: 0, scrollHeight: 0, scrollTop: 0, scrollLeft: 0 };
    }
    
    return {
      scrollWidth: element.scrollWidth || 0,
      scrollHeight: element.scrollHeight || 0,
      scrollTop: element.scrollTop || 0,
      scrollLeft: element.scrollLeft || 0
    };
  } catch (error) {
    console.warn('Failed to get scroll dimensions:', error);
    return { scrollWidth: 0, scrollHeight: 0, scrollTop: 0, scrollLeft: 0 };
  }
}

/**
 * Execute DOM measurement with retry logic
 */
export function withDOMReady<T>(callback: () => T, retries = 3, delay = 10): Promise<T> {
  return new Promise((resolve, reject) => {
    let attempt = 0;
    
    const tryMeasurement = () => {
      try {
        const result = callback();
        resolve(result);
      } catch (error) {
        attempt++;
        if (attempt < retries) {
          setTimeout(tryMeasurement, delay);
        } else {
          reject(error);
        }
      }
    };
    
    // Try immediately first, then with RAF if needed
    if (attempt === 0) {
      requestAnimationFrame(tryMeasurement);
    } else {
      tryMeasurement();
    }
  });
} 